import { Spinner } from '@frontend/components/common';
import { CommonMessage, getCorrectTranslation } from '@frontend/lang';
import { ProductType } from '@frontend/product/types';
import { LuCircleOff } from 'react-icons/lu';
import { FormattedMessage } from 'react-intl';

import WizardScreenContainer from '../../components/wizard-screen-container/wizard-screen-container.component';
import { ProductCard } from '../browse-products-by-categories/product-card/product-card.component';
import useConfirmShopTransaction from './confirm-shop-transaction.controller';

const ID = 'confirm-shop-transaction';

const ConfirmShopTransaction = () => {
    const viewProps = useConfirmShopTransaction();

    if (viewProps.isLoading) {
        return (
            <WizardScreenContainer
                id={ID}
                header={{ title: CommonMessage.WIZARD.SHOP.DEFAULT }}>
                <Spinner />
            </WizardScreenContainer>
        );
    }

    if (!viewProps.product) {
        return (
            <WizardScreenContainer
                footer={{ onBack: viewProps.onBack }}
                header={{ title: CommonMessage.WIZARD.SHOP.DEFAULT }}>
                <div>
                    <FormattedMessage
                        defaultMessage='No product selected'
                        id='confirm-lending.no-product-selected'
                    />
                </div>
            </WizardScreenContainer>
        );
    }

    return (
        <WizardScreenContainer
            id={ID}
            footer={{
                onBack: viewProps.onBack,
                onSubmit: viewProps.handleSubmit,
                submitLabel:
                    viewProps.product.type === ProductType.KIT ? (
                        CommonMessage.BUTTONS.CHECKOUT
                    ) : (
                        <FormattedMessage
                            defaultMessage='Take product'
                            id='confirm-lending.take-product'
                        />
                    ),
                submitDisabled:
                    viewProps.submitted ||
                    viewProps.isLoading ||
                    viewProps.hasWorkflowConfigurationError ||
                    !viewProps.hasCertification ||
                    (viewProps.isCostCenterRequired && !viewProps.selectedCostCenterId)
            }}
            loadingModal={{
                show: viewProps.submitted,
                onClose: () => viewProps.setSubmitted(false)
            }}
            header={{ title: CommonMessage.WIZARD.SHOP.DEFAULT }}>
            <div className='flex w-full flex-col gap-6'>
                <div className='flex flex-col items-center gap-3 self-stretch'>
                    <div className='flex w-full justify-between'>
                        <span className='text-foreground-default justify-center text-base font-medium'>
                            {viewProps.product.type === ProductType.KIT ? (
                                <FormattedMessage
                                    id='kiosk.wizard.confirm-shop-transaction.selected-kit'
                                    defaultMessage='Selected kit'
                                    description='Section label above the selected kit on the shop confirmation screen'
                                />
                            ) : (
                                <FormattedMessage
                                    defaultMessage='Selected product'
                                    id='confirm-lending.selected-product'
                                />
                            )}
                        </span>
                    </div>
                    <ProductCard
                        product={viewProps.product}
                        equipment={viewProps.equipment ?? undefined}
                        variant='attributes'
                        layout='vertical'
                    />
                    {viewProps.product.user_info && (
                        <div className='bg-background-surface border-border-default inline-flex w-full items-start justify-start gap-3 self-stretch rounded-xl border p-3'>
                            {getCorrectTranslation(viewProps.locale, viewProps.product, 'user_info')}
                        </div>
                    )}
                    {viewProps.product.type === ProductType.EQUIPMENT && !viewProps.hasCertification && (
                        <div className='bg-background-surface outline-border-default flex w-full items-center gap-3 rounded-xl border p-4 outline -outline-offset-1'>
                            <LuCircleOff className='text-foreground-default h-5 w-5' />
                            <div className='text-foreground-default text-base font-medium'>{CommonMessage.ERRORS.NOT_CERTIFIED}</div>
                        </div>
                    )}
                    {viewProps.hasWorkflowConfigurationError && (
                        <div className='bg-background-surface outline-border-default flex w-full items-center gap-3 rounded-xl border p-4 outline -outline-offset-1'>
                            <LuCircleOff className='text-foreground-default h-5 w-5' />
                            <div className='text-foreground-default text-base font-medium'>
                                <FormattedMessage
                                    id='kiosk.wizard.confirm-shop-transaction.workflow-configuration-error'
                                    description='Error shown when the Vending or Lending workflow required to fulfill a selected shop product is not configured'
                                    defaultMessage='This product cannot be fulfilled because its workflow is not configured. Please contact support.'
                                />
                            </div>
                        </div>
                    )}
                </div>

                {viewProps.kitItems.length > 0 && (
                    <div className='flex w-full flex-col gap-3'>
                        <span className='text-foreground-default text-base font-medium'>
                            <FormattedMessage
                                id='wizard.consume-kit.items'
                                defaultMessage='Kit items'
                                description='Section title for the list of products contained in the selected kit'
                            />
                        </span>
                        <div className='border-border-default bg-background-surface flex w-full flex-col overflow-hidden rounded-xl border'>
                            {viewProps.kitItems.map((item) => (
                                <div
                                    key={item.product.id}
                                    className='border-border-default flex w-full items-center justify-between gap-3 border-b px-3 py-2 last:border-b-0'>
                                    <span className='text-foreground-default text-sm font-medium'>
                                        {getCorrectTranslation(viewProps.locale, item.product, 'name') || item.product.ean}
                                    </span>
                                    <span className='text-foreground-muted shrink-0 text-sm font-semibold'>{item.quantity}x</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {viewProps.selectedCostCenterName && (
                    <div className='flex w-full flex-col gap-3'>
                        <span className='text-foreground-default text-base font-medium'>{CommonMessage.OBJECTS.COST_CENTER.DEFAULT}</span>
                        <div className='border-border-default bg-background-surface flex w-full items-center justify-between gap-3 rounded-xl border px-3 py-2'>
                            <span className='text-foreground-default text-sm font-medium'>{viewProps.selectedCostCenterName}</span>
                        </div>
                    </div>
                )}
            </div>
        </WizardScreenContainer>
    );
};

export default ConfirmShopTransaction;
